import { useLocation } from "react-router-dom";
import { TRANSACTION_REPORT, TRANSACTION_REPORT_COUNT } from "./fragment";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { userStore } from "../../../../store/user";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { TransactionReportRow } from "../transactions-report/type";
import { OrderItemReportRow } from "./type";
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { eventStore, useEventStore } from "../../../../store/event";
import { useOrganizationStore } from "../../../../store/organization";
import { useAccumulatedOptions, useLocationFilterOptions } from "../../components/useFilterOptions";
import { GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { buildReportCsvHeader } from "../../utils/buildReportCsvHeader";
import { SecondaryButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { colDefs } from "./helpers";
import Loader from "../../../../components/Loader";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;
const INITIAL_PAGE_SIZE = 1000;
const PAGE_SIZE = 50000;
const DEBOUNCE_DELAY = 100;

/**
 * Format the item_modifiers_applied array into a readable string.
 * e.g. "Blue (color), Male (gender), Small (size)"
 */
const formatModifiers = (modifiers: any[] | undefined | null): string => {
  if (!Array.isArray(modifiers) || !modifiers.length) return "";
  return modifiers
    .filter((m) => m.name != null)
    .map((m) => `${m.name}${m.sub_type ? ` (${m.sub_type})` : ""}`)
    .join(", ");
};

/**
 * Sum the additional_price from all applied modifiers (in cents).
 */
const sumModifierPrice = (modifiers: any[] | undefined | null): number => {
  if (!Array.isArray(modifiers) || !modifiers.length) return 0;
  return modifiers.reduce((sum, m) => sum + (m.additional_price ?? 0), 0);
};

/**
 * Build item ID string. If modifiers are applied, append their IDs separated by "-".
 * e.g. "49682-8-243-10"
 */
const buildItemId = (itemId: any, modifiers: any[] | undefined | null): string => {
  const base = itemId != null ? String(itemId) : "";
  if (!base) return "";
  if (!Array.isArray(modifiers) || !modifiers.length) return base;
  const modifierIds = modifiers
    .filter((m) => m.id != null)
    .map((m) => String(m.id));
  return [base, ...modifierIds].join("-");
};

/**
 * Explode transaction rows into individual item rows.
 * Handles both flat array and { cartItems: [] } formats for items_json.
 */
const explodeItems = (
  transactions: TransactionReportRow[]
): OrderItemReportRow[] => {
  const rows: OrderItemReportRow[] = [];
  for (const txn of transactions) {
    const json = txn.items_json as any;
    let items: any[] = [];
    if (Array.isArray(json)) {
      items = json;
    } else if (json?.cartItems && Array.isArray(json.cartItems)) {
      items = json.cartItems;
    }
    if (!items.length) continue;

    for (const item of items) {
      const qty = Math.max(1, item.quantity ?? 1);
      const row: OrderItemReportRow = {
        // Transaction-level fields
        order_id: txn.order_id,
        transaction_time: txn.transaction_time,
        transaction_date: (txn as any).transaction_date,
        transaction_hour: (txn as any).transaction_hour,
        username: txn.username,
        order_status: txn.order_status,
        payment_status: txn.payment_status,
        payment_type: txn.payment_type,
        order_mx_ref_id: txn.order_mx_ref_id,
        payment_ref_id: txn.payment_ref_id,
        transaction_amount: txn.transaction_amount,
        item_subtotal: txn.item_subtotal,
        tax: txn.tax,
        tip: txn.tip,
        digital_surcharge: txn.digital_surcharge,
        applied_discount_name: txn.applied_discount_name,
        applied_discount_amount: txn.applied_discount_amount,
        organization_name: txn.organization_name,
        event_name: txn.event_name,
        vendor_id: (txn as any).vendor_id,
        vendor_name: txn.vendor_name,
        location_id: (txn as any).location_id,
        location_name: txn.location_name,
        uid: txn.uid,
        last4: txn.last4,
        card_type: txn.card_type,
        email: txn.email,
        is_tab: txn.is_tab,
        tab_name: txn.tab_name,
        tab_nickname: txn.tab_nickname,
        created_at: txn.created_at,
        order_notes: txn.order_notes,
        // Item-level fields
        item_name: item.name ?? "",
        item_id: buildItemId(item.id, item.item_modifiers_applied),
        item_price: item.price ?? 0,
        item_category: item.category ?? "",
        item_tax: item.item_tax ?? 0,
        item_tax_type:
          item.tax_type ||
          (item.tax === "inclusive" ? "inclusive" : "exclusive"),
        item_tax_percentage: item.tax_percentage ?? 0,
        item_discount_applied: item.item_discount_applied ?? 0,
        item_token_value_charged: item.item_token_value_charged ?? 0,
        item_cash_balance_charged: item.item_cash_balance_charged ?? 0,
        item_promo_balance_charged: item.item_promo_balance_charged ?? 0,
        item_promo_discount_applied: item.item_promo_discount_applied ?? 0,
        // Modifier fields
        item_modifiers_applied: formatModifiers(item.item_modifiers_applied),
        item_modifiers_additional_price: sumModifierPrice(item.item_modifiers_applied),
      };

      // Expand by quantity: qty 3 = 3 identical rows
      for (let i = 0; i < qty; i++) {
        rows.push({ ...row });
      }
    }
  }
  return rows;
};

export const OrderItemsReport = () => {
  const [data, setData] = useState<OrderItemReportRow[]>([]);
  const filterEventId = useEventStore((state) => state.eventId);
  const filterOrganizationId = useOrganizationStore((state) => state.organizationId);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);

  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const location = useLocation();
  const fetchTimeoutRef = useRef<any>(null);

  // === Derived options (recompute when `data` changes) ===
  // Usernames: every value seen for this event, not only the current rows,
  // so a second pick is possible while the first filters the rows.
  const usernameOptions = useAccumulatedOptions(
    data,
    (r) => r.username,
    `${filterOrganizationId ?? ""}:${filterEventId ?? ""}`
  );

  // Locations: the event's active locations (the Vendor dropdown's source),
  // not the rows, which are already filtered by location.
  const locationOptions = useLocationFilterOptions();

  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      // Build ID→name maps for proper filter label resolution
      const vendorNameMap: Record<string, string> = {};
      const locationNameMap: Record<string, string> = {};
      for (const r of data) {
        if (r.vendor_id && r.vendor_name) vendorNameMap[String(r.vendor_id)] = r.vendor_name;
        if (r.location_id && r.location_name) locationNameMap[String(r.location_id)] = r.location_name;
      }
      const params = {
        fileName: `OrderItemsReport_${timestamp}.csv`,
        columnSeparator: ",",
        skipHeader: false,
        allColumns: true,
        prependContent: buildReportCsvHeader({
          reportName: "Order Items Report",
          searchString: location.search,
          vendorNameMap,
          locationNameMap,
        }),
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchOrderItems = async () => {
      setIsLoading(true);
      setIsFullyLoaded(false);
      try {
        const rawOrganizationId = getOrganizationState().organizationId;
        const organizationId = parseInt(
          rawOrganizationId !== null ? rawOrganizationId.toString() : "",
          10
        );
        const { eventId } = getEventState();
        const user = getState().user;

        const search = new URLSearchParams(location.search);
        const where = search.get("where");
        // RP-1937: no filter in the address yet; ReportFilters re-applies it and this runs again.
        if (!where) return;
        let whereVariable: any = where ? JSON.parse(where) : {};

        if (
          organizationId &&
          !Number.isNaN(organizationId) &&
          organizationId !== 0
        ) {
          whereVariable = {
            ...whereVariable,
            organization_id: { _eq: organizationId },
          };
        }

        if (user?.vendors && !whereVariable.vendor_id) {
          whereVariable = {
            ...whereVariable,
            vendor_id: { _in: user.vendors },
          };
        }

        if (eventId) {
          whereVariable = {
            ...whereVariable,
            event_id: { _eq: eventId },
          };
        } else if (user?.events?.length) {
          whereVariable = {
            ...whereVariable,
            event_id: { _in: user.events },
          };
        }

        whereVariable = {
          ...whereVariable,
          payment_status: { _nin: ["pending", "pending1"] },
        };

        // Get total count
        const { data: totalData } = await client.query({
          query: TRANSACTION_REPORT_COUNT,
          variables: { where: whereVariable, limit: 0 },
        });
        const totalCount = totalData.total.aggregate.count as number;

        // === Initial fast load (first 1000 transaction rows) ===
        const initialLimit = Math.min(INITIAL_PAGE_SIZE, totalCount);
        const { data: initialChunk } = await client.query({
          query: TRANSACTION_REPORT,
          variables: {
            where: whereVariable,
            limit: initialLimit,
            offset: 0,
          },
        });

        const initialItems = explodeItems(
          initialChunk.reports_transactions || []
        );
        if (isMounted) {
          setData(initialItems);
          setIsLoading(false);
        }

        // === Background loading of remaining data ===
        if (initialLimit < totalCount) {
          let offset = initialLimit;
          let backgroundItems: OrderItemReportRow[] = [];

          while (offset < totalCount) {
            if (!isMounted) return;

            const { data: chunk } = await client.query({
              query: TRANSACTION_REPORT,
              variables: {
                where: whereVariable,
                limit: PAGE_SIZE,
                offset,
              },
            });

            const chunkItems = explodeItems(
              chunk.reports_transactions || []
            );
            backgroundItems = [...backgroundItems, ...chunkItems];

            // Append to grid as each chunk arrives
            if (isMounted) {
              setData((prev) => [...prev, ...chunkItems]);
            }

            offset += PAGE_SIZE;
          }
        }

        if (isMounted) {
          setIsFullyLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching order items:", error);
        if (isMounted) {
          setIsLoading(false);
          setIsFullyLoaded(true);
        }
      }
    };

    const debouncedFetch = () => {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchOrderItems, DEBOUNCE_DELAY);
    };

    debouncedFetch();

    return () => {
      isMounted = false;
      clearTimeout(fetchTimeoutRef.current);
    };
  }, [location.search]);

  const gridOptions = { enableCellTextSelection: true };

  return (
    <>
      <div className="bg-white px-4 pb-1 mb-3 rounded-md">
        <ReportHeader title="Order Items Report" />
        <ReportFilters
          includeSubEvents
          includeDates
          includeTime
          includeLocations
          includeVendors
          eventScopedVendors
          includeUsernames
          usernameOptions={usernameOptions}
          locationOptions={locationOptions}
          customQueryPaths={{
            dateFrom: "_and[0].transaction_hour",
            dateTo: "_and[1].transaction_hour",
          }}
        />
        <div className="flex">
          <div className="ml-auto">
            <SecondaryButton
              onClick={handleExportCSV}
              isDisabled={!isFullyLoaded}
            >
              {isFullyLoaded ? (
                "Export to CSV"
              ) : (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Export to CSV (Loading...)
                </span>
              )}
            </SecondaryButton>
          </div>
        </div>
      </div>

      <div style={{ height: "60vh" }}>
        <div
          className={"ag-theme-quartz"}
          style={{ width: "100%", height: "100%" }}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <AgGridReact
              rowData={data}
              columnDefs={colDefs}
              rowHeight={rowHeight}
              autoSizeStrategy={autoSizeStrategy}
              pagination={true}
              gridOptions={gridOptions}
              paginationPageSize={100}
              onGridReady={(params) => (gridRef.current = params.api)}
            />
          )}
        </div>
      </div>
    </>
  );
};
